// Contract Types for ITExperts Portal

// ============================================================================
// Enums
// ============================================================================

export type ContractStatus =
  | 'draft'
  | 'pending'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'disputed'
  | 'terminated';

export type MilestoneStatus =
  | 'pending'
  | 'in_progress'
  | 'submitted'
  | 'revision_requested'
  | 'approved'
  | 'paid'
  | 'cancelled';

export type SubmissionStatus =
  | 'pending_review'
  | 'approved'
  | 'revision_requested'
  | 'rejected';

export type DisputeStatus =
  | 'open'
  | 'under_review'
  | 'resolved_company'
  | 'resolved_expert'
  | 'resolved_split'
  | 'withdrawn';

export type DisputeType =
  | 'quality'
  | 'scope'
  | 'deadline'
  | 'payment'
  | 'communication'
  | 'other';

// ============================================================================
// Contract
// ============================================================================

export interface Contract {
  id: string;
  company_id: string;
  expert_id: string;
  job_id?: string;
  job_application_id?: string;
  title: string;
  description?: string;
  scope_of_work: string;
  total_amount_cents: number;
  currency: string;
  platform_fee_rate: number;
  start_date?: string;
  end_date?: string;
  status: ContractStatus;
  terms_and_conditions?: string;
  payment_terms?: string;
  cancellation_policy?: string;
  company_signed_at?: string;
  company_signed_by?: string;
  company_signature_ip?: string;
  expert_signed_at?: string;
  expert_signed_by?: string;
  expert_signature_ip?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  cancelled_by?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  milestones?: Milestone[];
}

// ============================================================================
// Contract Summary (for list views)
// ============================================================================

export interface ContractSummary {
  id: string;
  company_id: string;
  expert_id: string;
  title: string;
  status: ContractStatus;
  total_amount_cents: number;
  currency: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  total_milestones: number;
  paid_milestones: number;
  pending_milestones: number;
  submitted_milestones: number;
  paid_amount_cents: number;
  open_disputes: number;
  unread_messages: number;
}

// ============================================================================
// Milestone
// ============================================================================

export interface Milestone {
  id: string;
  contract_id: string;
  title: string;
  description?: string;
  deliverables?: string;
  acceptance_criteria?: string;
  sequence_number: number;
  amount_cents: number;
  platform_fee_cents: number;
  expert_amount_cents: number;
  due_date?: string;
  status: MilestoneStatus;
  escrow_id?: string;
  started_at?: string;
  submitted_at?: string;
  approved_at?: string;
  paid_at?: string;
  revision_count: number;
  max_revisions: number;
  created_at: string;
  updated_at: string;
  submissions?: MilestoneSubmission[];
}

// ============================================================================
// Milestone Submission
// ============================================================================

export interface MilestoneSubmission {
  id: string;
  milestone_id: string;
  submitted_by: string;
  submission_notes?: string;
  deliverable_urls?: string[];
  attachments?: Attachment[];
  status: SubmissionStatus;
  reviewed_at?: string;
  reviewed_by?: string;
  review_notes?: string;
  revision_number: number;
  previous_submission_id?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Milestone Review
// ============================================================================

export interface MilestoneReview {
  id: string;
  submission_id: string;
  reviewer_id: string;
  decision: SubmissionStatus;
  feedback?: string;
  quality_rating?: number;
  communication_rating?: number;
  timeliness_rating?: number;
  revision_items?: RevisionItem[];
  created_at: string;
}

export interface RevisionItem {
  description: string;
  priority?: 'high' | 'medium' | 'low';
}

export interface Attachment {
  name: string;
  url: string;
  mime_type?: string;
  size?: number;
}

// ============================================================================
// Dispute
// ============================================================================

export interface Dispute {
  id: string;
  contract_id: string;
  milestone_id?: string;
  dispute_type: DisputeType;
  title: string;
  description: string;
  evidence_urls?: string[];
  attachments?: Attachment[];
  raised_by: string;
  raised_by_type: 'company' | 'expert';
  status: DisputeStatus;
  resolution_notes?: string;
  resolution_amount_cents?: number;
  resolved_by?: string;
  resolved_at?: string;
  escalated_at?: string;
  escalation_reason?: string;
  created_at: string;
  updated_at: string;
  responses?: DisputeResponse[];
}

export interface DisputeResponse {
  id: string;
  dispute_id: string;
  responder_id: string;
  responder_type: 'company' | 'expert' | 'platform';
  message: string;
  evidence_urls?: string[];
  attachments?: Attachment[];
  created_at: string;
}

// ============================================================================
// Contract Message
// ============================================================================

export interface ContractMessage {
  id: string;
  contract_id: string;
  sender_id: string;
  sender_type: 'company' | 'expert' | 'system';
  message: string;
  attachments?: Attachment[];
  milestone_id?: string;
  read_at?: string;
  read_by?: string;
  is_system_message: boolean;
  created_at: string;
}

// ============================================================================
// Request Types
// ============================================================================

export interface CreateContractRequest {
  expert_id: string;
  job_id?: string;
  job_application_id?: string;
  title: string;
  description?: string;
  scope_of_work: string;
  total_amount_cents: number;
  currency?: string;
  start_date?: string;
  end_date?: string;
  terms_and_conditions?: string;
  payment_terms?: string;
  cancellation_policy?: string;
  milestones: CreateMilestoneRequest[];
}

export interface CreateMilestoneRequest {
  title: string;
  description?: string;
  deliverables?: string;
  acceptance_criteria?: string;
  amount_cents: number;
  due_date?: string;
  max_revisions?: number;
}

export interface UpdateContractRequest {
  title?: string;
  description?: string;
  scope_of_work?: string;
  start_date?: string;
  end_date?: string;
  terms_and_conditions?: string;
  payment_terms?: string;
  cancellation_policy?: string;
}

export interface SignContractRequest {
  acknowledged: boolean;
}

export interface SubmitMilestoneRequest {
  submission_notes?: string;
  deliverable_urls?: string[];
}

export interface ReviewSubmissionRequest {
  decision: SubmissionStatus;
  feedback?: string;
  quality_rating?: number;
  communication_rating?: number;
  timeliness_rating?: number;
  revision_items?: RevisionItem[];
}

export interface CreateDisputeRequest {
  milestone_id?: string;
  dispute_type: DisputeType;
  title: string;
  description: string;
  evidence_urls?: string[];
}

export interface RespondToDisputeRequest {
  message: string;
  evidence_urls?: string[];
}

export interface SendMessageRequest {
  message: string;
  milestone_id?: string;
}

export interface FundMilestoneRequest {
  payment_method_id: string;
}

// ============================================================================
// Response Types
// ============================================================================

export interface ContractListResponse {
  contracts: ContractSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface MessageListResponse {
  messages: ContractMessage[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

import { formatCurrency } from './payment';

/**
 * Get human-readable contract status
 */
export function getContractStatusLabel(status: ContractStatus): string {
  const labels: Record<ContractStatus, string> = {
    draft: 'Draft',
    pending: 'Pending Signatures',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    disputed: 'Disputed',
    terminated: 'Terminated',
  };
  return labels[status] || status;
}

/**
 * Get status color for contract
 */
export function getContractStatusColor(status: ContractStatus): string {
  const colors: Record<ContractStatus, string> = {
    draft: 'gray',
    pending: 'yellow',
    active: 'blue',
    completed: 'green',
    cancelled: 'gray',
    disputed: 'red',
    terminated: 'red',
  };
  return colors[status] || 'gray';
}

/**
 * Get human-readable milestone status
 */
export function getMilestoneStatusLabel(status: MilestoneStatus): string {
  const labels: Record<MilestoneStatus, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    submitted: 'Submitted',
    revision_requested: 'Revision Requested',
    approved: 'Approved',
    paid: 'Paid',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get status color for milestone
 */
export function getMilestoneStatusColor(status: MilestoneStatus): string {
  const colors: Record<MilestoneStatus, string> = {
    pending: 'gray',
    in_progress: 'blue',
    submitted: 'yellow',
    revision_requested: 'orange',
    approved: 'green',
    paid: 'green',
    cancelled: 'gray',
  };
  return colors[status] || 'gray';
}

/**
 * Get human-readable dispute type
 */
export function getDisputeTypeLabel(type: DisputeType): string {
  const labels: Record<DisputeType, string> = {
    quality: 'Quality Issue',
    scope: 'Scope Dispute',
    deadline: 'Deadline Issue',
    payment: 'Payment Dispute',
    communication: 'Communication Issue',
    other: 'Other',
  };
  return labels[type] || type;
}

/**
 * Get human-readable dispute status
 */
export function getDisputeStatusLabel(status: DisputeStatus): string {
  const labels: Record<DisputeStatus, string> = {
    open: 'Open',
    under_review: 'Under Review',
    resolved_company: 'Resolved (Company)',
    resolved_expert: 'Resolved (Expert)',
    resolved_split: 'Resolved (Split)',
    withdrawn: 'Withdrawn',
  };
  return labels[status] || status;
}

/**
 * Calculate contract progress percentage
 */
export function getContractProgress(summary: ContractSummary): number {
  if (summary.total_milestones === 0) return 0;
  return Math.round((summary.paid_milestones / summary.total_milestones) * 100);
}

/**
 * Format contract amount
 */
export function formatContractAmount(
  amountCents: number,
  currency: string = 'EUR'
): string {
  return formatCurrency(amountCents, currency);
}

/**
 * Check if contract can be signed by expert
 */
export function canExpertSign(contract: Contract): boolean {
  return (
    contract.status === 'pending' &&
    contract.company_signed_at !== undefined &&
    contract.expert_signed_at === undefined
  );
}

/**
 * Check if milestone can be started
 */
export function canStartMilestone(milestone: Milestone, contract: Contract): boolean {
  return contract.status === 'active' && milestone.status === 'pending';
}

/**
 * Check if milestone can be submitted
 */
export function canSubmitMilestone(milestone: Milestone, contract: Contract): boolean {
  return (
    contract.status === 'active' &&
    (milestone.status === 'in_progress' || milestone.status === 'revision_requested')
  );
}

/**
 * Format date for display
 */
export function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-IE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format datetime for display
 */
export function formatDateTime(dateString?: string): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-IE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
